import React, { useRef, useCallback } from "react";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import { useDAW } from "./DAWProvider";
import WaveformDisplay from "./WaveformDisplay";
import AutomationLane from "./AutomationLane";
import { PIXELS_PER_BEAT } from "./TimelineRuler";
import { MIDI_MIN, MIDI_MAX } from "./midiConstants";

const EDGE_HIT_ZONE = 8; // px

function useClipDrag({ pxPerSec, onMove, onTrimLeft, onTrimRight, onDuplicate }) {
  const dragRef = useRef(null);

  const handlePointerDown = useCallback((e) => {
    e.stopPropagation();
    const clipEl = e.currentTarget;
    const rect = clipEl.getBoundingClientRect();
    const localX = e.clientX - rect.left;
    const isLeftEdge = localX < EDGE_HIT_ZONE;
    const isRightEdge = localX > rect.width - EDGE_HIT_ZONE;

    let mode = "move";
    if (isLeftEdge && onTrimLeft) mode = "trim-left";
    else if (isRightEdge && onTrimRight) mode = "trim-right";

    // Option+drag = duplicate first, then drag original
    if (e.altKey && mode === "move" && onDuplicate) {
      onDuplicate();
    }

    dragRef.current = {
      mode,
      startX: e.clientX,
      startLeft: clipEl.offsetLeft,
      clipWidth: rect.width,
    };

    clipEl.setPointerCapture(e.pointerId);
  }, [onTrimLeft, onTrimRight, onDuplicate]);

  const handlePointerMove = useCallback((e) => {
    if (!dragRef.current) return;
    const { mode, startX } = dragRef.current;
    const deltaX = e.clientX - startX;
    const deltaSec = deltaX / pxPerSec;
    const snapToGrid = !e.shiftKey;

    if (mode === "move" && onMove) {
      onMove(deltaSec, snapToGrid);
    } else if (mode === "trim-left" && onTrimLeft) {
      onTrimLeft(deltaSec);
    } else if (mode === "trim-right" && onTrimRight) {
      onTrimRight(deltaSec);
    }
  }, [pxPerSec, onMove, onTrimLeft, onTrimRight]);

  const handlePointerUp = useCallback((e) => {
    if (!dragRef.current) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    dragRef.current = null;
  }, []);

  return { handlePointerDown, handlePointerMove, handlePointerUp };
}

function DrumClipPreview({ pattern, kit, totalSteps, clipWidthPx, leftPx, pxPerSec, bpm }) {
  const { drumsStartTime, setDrumsStartTime, setSelectedTrackId } = useDAW();
  const startRef = useRef(drumsStartTime);
  const beatDuration = 60 / bpm;

  const onMove = useCallback((deltaSec, snap) => {
    let newStart = startRef.current + deltaSec;
    if (snap) newStart = Math.round(newStart / beatDuration) * beatDuration;
    setDrumsStartTime(Math.max(0, newStart));
  }, [beatDuration, setDrumsStartTime]);

  const drag = useClipDrag({ pxPerSec, onMove, onTrimLeft: null, onTrimRight: null });

  const handlePointerDown = useCallback((e) => {
    startRef.current = drumsStartTime;
    drag.handlePointerDown(e);
  }, [drumsStartTime, drag]);

  return (
    <div
      className="daw-clip daw-clip--drums daw-clip--positioned"
      style={{ width: clipWidthPx, left: leftPx }}
      onPointerDown={handlePointerDown}
      onPointerMove={drag.handlePointerMove}
      onPointerUp={drag.handlePointerUp}
      onClick={(e) => { e.stopPropagation(); setSelectedTrackId("drums"); }}
    >
      <div className="daw-clip__edge daw-clip__edge--left" />
      <span className="daw-clip__label">Drums</span>
      <div className="daw-clip__pattern-preview">
        {pattern.map((row, padIndex) => {
          const color = kit.pads[padIndex]?.color || "#EE772F";
          const stepWidth = clipWidthPx / totalSteps;
          return row.map((isOn, step) =>
            isOn ? (
              <div
                key={`${padIndex}-${step}`}
                className="daw-clip__dot"
                style={{ left: step * stepWidth, top: padIndex * 3, background: color }}
              />
            ) : null
          );
        })}
      </div>
      <div className="daw-clip__edge daw-clip__edge--right" />
    </div>
  );
}

function AudioClipPreview({ track, pxPerSec, bpm }) {
  const { setAudioTrackPosition, setAudioTrackTrim, setSelectedTrackId, duplicateAudioTrack } = useDAW();
  const startRef = useRef({ startTime: track.startTime, trimStart: track.trimStart, trimEnd: track.trimEnd });

  const bufferDuration = track.buffer ? track.buffer.duration : 0;
  const trimmedDuration = (track.trimEnd - track.trimStart) * bufferDuration;
  const clipWidthPx = trimmedDuration * pxPerSec;
  const leftPx = track.startTime * pxPerSec;
  const beatDuration = 60 / bpm;

  const onMove = useCallback((deltaSec, snap) => {
    let newStart = startRef.current.startTime + deltaSec;
    if (snap) newStart = Math.round(newStart / beatDuration) * beatDuration;
    setAudioTrackPosition(track.id, Math.max(0, newStart));
  }, [track.id, beatDuration, setAudioTrackPosition]);

  const onTrimLeft = useCallback((deltaSec) => {
    const deltaNorm = bufferDuration > 0 ? deltaSec / bufferDuration : 0;
    const origStart = startRef.current.trimStart;
    const origEnd = startRef.current.trimEnd;
    const newTrimStart = Math.max(0, Math.min(origEnd - 0.01, origStart + deltaNorm));
    setAudioTrackTrim(track.id, newTrimStart, origEnd);
    const origStartTime = startRef.current.startTime;
    const timeDelta = (newTrimStart - origStart) * bufferDuration;
    setAudioTrackPosition(track.id, Math.max(0, origStartTime + timeDelta));
  }, [track.id, bufferDuration, setAudioTrackTrim, setAudioTrackPosition]);

  const onTrimRight = useCallback((deltaSec) => {
    const deltaNorm = bufferDuration > 0 ? deltaSec / bufferDuration : 0;
    const origStart = startRef.current.trimStart;
    const origEnd = startRef.current.trimEnd;
    const newTrimEnd = Math.max(origStart + 0.01, Math.min(1, origEnd + deltaNorm));
    setAudioTrackTrim(track.id, origStart, newTrimEnd);
  }, [track.id, bufferDuration, setAudioTrackTrim]);

  const onDuplicate = useCallback(() => {
    duplicateAudioTrack(track.id);
  }, [track.id, duplicateAudioTrack]);

  const drag = useClipDrag({ pxPerSec, onMove, onTrimLeft, onTrimRight, onDuplicate });

  const handlePointerDown = useCallback((e) => {
    startRef.current = { startTime: track.startTime, trimStart: track.trimStart, trimEnd: track.trimEnd };
    drag.handlePointerDown(e);
  }, [track.startTime, track.trimStart, track.trimEnd, drag]);

  if (!track.buffer) return null;

  return (
    <div
      className="daw-clip daw-clip--audio daw-clip--positioned"
      style={{ width: Math.max(20, clipWidthPx), left: leftPx }}
      onPointerDown={handlePointerDown}
      onPointerMove={drag.handlePointerMove}
      onPointerUp={drag.handlePointerUp}
      onClick={(e) => { e.stopPropagation(); setSelectedTrackId(track.id); }}
    >
      <div className="daw-clip__edge daw-clip__edge--left" />
      <span className="daw-clip__label">{track.name}</span>
      <div className="daw-clip__waveform">
        <WaveformDisplay audioBuffer={track.buffer} height={36} trimStart={track.trimStart} trimEnd={track.trimEnd} />
      </div>
      <div className="daw-clip__edge daw-clip__edge--right" />
    </div>
  );
}

function InstrumentClipPreview({ track, pxPerSec, bpm }) {
  const { setInstrumentTrackPosition, setSelectedTrackId, duplicateInstrumentTrack } = useDAW();
  const startRef = useRef(track.startTime);
  const beatDuration = 60 / bpm;

  // Compute clip dimensions from note content
  const maxNoteEnd = track.notes.length > 0
    ? Math.max(...track.notes.map((n) => n.start + n.duration))
    : 4; // default 4 beats if no notes
  const clipDurationSec = maxNoteEnd * beatDuration;
  const clipWidthPx = Math.max(60, clipDurationSec * pxPerSec);
  const leftPx = (track.startTime ?? 0) * pxPerSec;

  const onMove = useCallback((deltaSec, snap) => {
    let newStart = startRef.current + deltaSec;
    if (snap) newStart = Math.round(newStart / beatDuration) * beatDuration;
    setInstrumentTrackPosition(track.id, Math.max(0, newStart));
  }, [track.id, beatDuration, setInstrumentTrackPosition]);

  const onDuplicate = useCallback(() => {
    duplicateInstrumentTrack(track.id);
  }, [track.id, duplicateInstrumentTrack]);

  const drag = useClipDrag({ pxPerSec, onMove, onTrimLeft: null, onTrimRight: null, onDuplicate });

  const handlePointerDown = useCallback((e) => {
    startRef.current = track.startTime ?? 0;
    drag.handlePointerDown(e);
  }, [track.startTime, drag]);

  const midiRange = MIDI_MAX - MIDI_MIN;
  const clipHeight = 48;

  return (
    <div
      className="daw-clip daw-clip--instrument daw-clip--positioned"
      style={{ width: clipWidthPx, left: leftPx }}
      onPointerDown={handlePointerDown}
      onPointerMove={drag.handlePointerMove}
      onPointerUp={drag.handlePointerUp}
      onClick={(e) => { e.stopPropagation(); setSelectedTrackId(track.id); }}
    >
      <div className="daw-clip__edge daw-clip__edge--left" />
      <span className="daw-clip__label">{track.name}</span>
      <div className="daw-clip__note-preview">
        {track.notes.map((note) => {
          const x = (note.start / maxNoteEnd) * clipWidthPx;
          const w = Math.max(2, (note.duration / maxNoteEnd) * clipWidthPx);
          const y = clipHeight - ((note.midi - MIDI_MIN) / midiRange) * clipHeight;
          return (
            <div
              key={note.id}
              className="daw-clip__note-rect"
              style={{ left: x, top: y, width: w }}
            />
          );
        })}
      </div>
      <div className="daw-clip__edge daw-clip__edge--right" />
    </div>
  );
}

export default function ArrangementTrack({ type, track }) {
  const {
    kit, pattern, totalSteps, numBars, bpm, arrangementBars,
    drumsMuted, setDrumsMuted, drumsStartTime,
    selectedTrackId, setSelectedTrackId,
    toggleAudioTrackMute, toggleAudioTrackSolo, removeAudioTrack, setAudioTrackVolume,
    toggleInstrumentTrackMute, toggleInstrumentTrackSolo, removeInstrumentTrack, setInstrumentTrackVolume,
    toggleInstrumentTrackRecordArm, toggleTrackAutomationVisible,
    canDelete, canEditTransport,
  } = useDAW();

  const beatsPerBar = 4;
  const totalBeats = arrangementBars * beatsPerBar;
  const totalWidth = totalBeats * PIXELS_PER_BEAT;
  const totalDuration = arrangementBars * (60 / bpm) * 4;
  const pxPerSec = totalDuration > 0 ? totalWidth / totalDuration : 1;

  const isDrums = type === "drums";
  const isInstrument = type === "instrument";
  const isAudio = type === "audio";
  const trackId = isDrums ? "drums" : track.id;
  const isSelected = selectedTrackId === trackId;
  const isMuted = isDrums ? drumsMuted : track?.muted;

  const drumClipWidthPx = numBars * beatsPerBar * PIXELS_PER_BEAT;
  const drumLeftPx = drumsStartTime * pxPerSec;

  const handleMute = (e) => {
    e.stopPropagation();
    if (isDrums) setDrumsMuted(!drumsMuted);
    else if (isInstrument) toggleInstrumentTrackMute(track.id);
    else toggleAudioTrackMute(track.id);
  };

  const handleSolo = (e) => {
    e.stopPropagation();
    if (isInstrument) toggleInstrumentTrackSolo(track.id);
    else if (isAudio) toggleAudioTrackSolo(track.id);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (isInstrument) removeInstrumentTrack(track.id);
    else if (isAudio) removeAudioTrack(track.id);
  };

  const handleVolume = (_, val) => {
    if (isInstrument) setInstrumentTrackVolume(track.id, val);
    else if (isAudio) setAudioTrackVolume(track.id, val);
  };

  const trackColor = isDrums ? "#EE772F" : isInstrument ? "#ba68c8" : undefined;

  return (
    <div
      className={`daw-arr-track${isSelected ? " daw-arr-track--selected" : ""}${isMuted ? " daw-arr-track--muted" : ""}`}
      onClick={() => setSelectedTrackId(trackId)}
    >
      <div className="daw-arr-track__header">
        <span className="daw-arr-track__name" style={trackColor ? { color: trackColor } : undefined}>
          {isDrums ? "Drums" : track.name}
        </span>
        <div className="daw-arr-track__controls">
          {isInstrument && (
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); toggleInstrumentTrackRecordArm(track.id); }}
              sx={{
                color: track?.recordArmed ? "#f44336" : "#555",
                padding: "2px",
                animation: track?.recordArmed ? "pulse-record 1.2s infinite" : "none",
              }}
              title={track?.recordArmed ? "Disarm recording" : "Arm recording"}
            >
              <FiberManualRecordIcon sx={{ fontSize: 14 }} />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={handleMute}
            sx={{ color: isMuted ? "#e53935" : "#555", padding: "2px" }}
            title={isMuted ? "Unmute" : "Mute"}
          >
            {isMuted ? <VolumeOffIcon sx={{ fontSize: 14 }} /> : <VolumeUpIcon sx={{ fontSize: 14 }} />}
          </IconButton>
          {!isDrums && (
            <>
              <IconButton
                size="small"
                onClick={handleSolo}
                sx={{ color: track?.solo ? "#fdd835" : "#555", padding: "2px" }}
                title={track?.solo ? "Unsolo" : "Solo"}
              >
                <HeadphonesIcon sx={{ fontSize: 14 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); toggleTrackAutomationVisible(track.id); }}
                sx={{ color: track?.automationVisible ? "#fdd835" : "#555", padding: "2px" }}
                title={track?.automationVisible ? "Hide automation" : "Show automation"}
              >
                <ShowChartIcon sx={{ fontSize: 14 }} />
              </IconButton>
              {canDelete && (
                <IconButton
                  size="small"
                  onClick={handleDelete}
                  sx={{ color: "#555", padding: "2px", "&:hover": { color: "#e53935" } }}
                  title="Delete"
                >
                  <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </>
          )}
        </div>
        {!isDrums && (
          <Slider
            value={track.volume}
            onChange={handleVolume}
            min={0}
            max={100}
            size="small"
            sx={{ width: "100%", color: "#555", mt: 0.5, "& .MuiSlider-thumb": { width: 10, height: 10 } }}
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </div>

      <div className="daw-arr-track__content">
        <div className="daw-arr-track__clips" style={{ minWidth: totalWidth }}>
          {isDrums && (
            <DrumClipPreview
              pattern={pattern} kit={kit} totalSteps={totalSteps}
              clipWidthPx={drumClipWidthPx} leftPx={drumLeftPx}
              pxPerSec={pxPerSec} bpm={bpm}
            />
          )}
          {isAudio && <AudioClipPreview track={track} pxPerSec={pxPerSec} bpm={bpm} />}
          {isInstrument && <InstrumentClipPreview track={track} pxPerSec={pxPerSec} bpm={bpm} />}
        </div>
        {!isDrums && track?.automationVisible && (
          <div className="daw-arr-track__automation" style={{ minWidth: totalWidth }}>
            <AutomationLane trackId={track.id} automation={track.automation || []} totalWidth={totalWidth} bpm={bpm} />
          </div>
        )}
      </div>
    </div>
  );
}
